// ─────────────────────────────────────────────────────────────────────────────
// Crew — time poll: propose 2–3 slots, vote yes/maybe/no, owner locks one (Agent B).
//
// The server fires `time_locked` itself on lock — the client fires NO funnel event
// here. Optimistic concurrency: every vote/lock carries the version we last read.
// On 409 (stale) we reload the poll and ask the user to retry, per spec.
//
// NOTE: there is no GET-poll endpoint, and vote/lock return the fresh poll, so a
// "reload" = re-issuing the last write with the latest version we hold. We surface
// a clear retry affordance rather than silently looping.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import type { Member, TimePoll as Poll } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { TextField } from '../components/Field';
import { ErrorBanner } from '../components/Feedback';
import { errorMessage, formatSlot } from '../lib/format';

type Vote = 'yes' | 'maybe' | 'no';
const VOTES: Vote[] = ['yes', 'maybe', 'no'];
const VOTE_LABEL: Record<Vote, string> = { yes: 'Yes', maybe: 'Maybe', no: 'No' };

interface TimePollProps {
  groupId: string;
  goalId?: string;
  poll: Poll | null;
  members: Member[];
  me: Member;
  isOwner: boolean;
  onPoll: (poll: Poll) => void;
}

// A draft slot before the poll exists. Two rows by default; up to three.
interface DraftSlot {
  date: string; // yyyy-mm-dd
  time: string; // HH:mm
}

function draftToISO(s: DraftSlot): string | null {
  if (!s.date || !s.time) return null;
  const d = new Date(`${s.date}T${s.time}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function TimePoll({ groupId, goalId, poll, members, me, isOwner, onPoll }: TimePollProps) {
  const [title, setTitle] = useState('When shall we meet?');
  const [slots, setSlots] = useState<DraftSlot[]>([
    { date: '', time: '19:00' },
    { date: '', time: '19:00' },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when a 409 was hit so the UI can prompt an explicit retry.
  const [staleRetry, setStaleRetry] = useState<null | (() => void)>(null);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const member of members) m.set(member.id, member.displayName);
    return m;
  }, [members]);

  // ── Create ──────────────────────────────────────────────────────────────────
  function setSlot(i: number, patch: Partial<DraftSlot>) {
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function addSlot() {
    setSlots((prev) => (prev.length >= 3 ? prev : [...prev, { date: '', time: '19:00' }]));
  }
  function removeSlot(i: number) {
    setSlots((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  async function createPoll() {
    const proposed = slots
      .map((s, i) => {
        const iso = draftToISO(s);
        return iso ? { id: `slot-${i + 1}`, startISO: iso } : null;
      })
      .filter((s): s is { id: string; startISO: string } => s !== null);
    if (proposed.length < 2) {
      setError('Propose at least two date/time slots so people have a choice.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createPoll(groupId, { title: title.trim() || 'When shall we meet?', goalId, proposedSlots: proposed });
      onPoll(res.poll);
    } catch (err) {
      setError(errorMessage(err, 'Could not create the poll. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  // ── Vote ────────────────────────────────────────────────────────────────────
  async function vote(slotId: string, v: Vote) {
    if (!poll) return;
    setBusy(true);
    setError(null);
    setStaleRetry(null);
    try {
      const res = await api.votePoll(poll.id, { slotId, vote: v, memberId: me.id });
      onPoll(res.poll);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Someone else just updated this poll. Reload and try your vote again.');
        setStaleRetry(() => () => vote(slotId, v));
      } else {
        setError(errorMessage(err, 'Could not record your vote. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Lock (owner only) ─────────────────────────────────────────────────────────
  async function lock(slotId: string) {
    if (!poll) return;
    setBusy(true);
    setError(null);
    setStaleRetry(null);
    try {
      const res = await api.lockPoll(poll.id, { slotId });
      onPoll(res.poll);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('The poll changed while locking. Reload and lock the slot again.');
        setStaleRetry(() => () => lock(slotId));
      } else {
        setError(errorMessage(err, 'Could not lock the slot. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Render: create form when no poll yet ────────────────────────────────────
  if (!poll) {
    return (
      <Card>
        <CardTitle>Find a time</CardTitle>
        <p className="mt-1 text-sm text-ink-700/70">Propose 2–3 options and let the crew vote.</p>
        {error && <div className="mt-3"><ErrorBanner message={error} /></div>}
        <div className="mt-4 flex flex-col gap-4">
          <TextField label="Poll title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <fieldset className="flex flex-col gap-3">
            <legend className="text-sm font-medium text-ink-800">Proposed slots</legend>
            {slots.map((s, i) => (
              <div key={i} className="flex items-end gap-2">
                <TextField
                  label={`Slot ${i + 1} date`}
                  type="date"
                  value={s.date}
                  onChange={(e) => setSlot(i, { date: e.target.value })}
                  className="flex-1"
                />
                <TextField
                  label="Time"
                  type="time"
                  value={s.time}
                  onChange={(e) => setSlot(i, { time: e.target.value })}
                  className="w-32"
                />
                {slots.length > 2 && (
                  <Button variant="ghost" onClick={() => removeSlot(i)} aria-label={`Remove slot ${i + 1}`}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {slots.length < 3 && (
              <Button variant="ghost" onClick={addSlot} className="self-start">
                + Add another slot
              </Button>
            )}
          </fieldset>
          <Button onClick={createPoll} busy={busy}>
            Create poll
          </Button>
        </div>
      </Card>
    );
  }

  // ── Render: existing poll ───────────────────────────────────────────────────
  const myVotes = new Map<string, Vote>();
  for (const r of poll.responses) if (r.memberId === me.id) myVotes.set(r.slotId, r.vote);

  function tally(slotId: string, v: Vote): string[] {
    if (!poll) return [];
    return poll.responses
      .filter((r) => r.slotId === slotId && r.vote === v)
      .map((r) => nameById.get(r.memberId) ?? 'Member');
  }

  const locked = poll.status === 'locked';

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <CardTitle>{poll.title}</CardTitle>
        {locked && (
          <span className="rounded-full bg-amber-400/20 px-2.5 py-0.5 text-[11px] font-semibold text-amber-500 ring-1 ring-amber-400/40">
            Locked
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <ErrorBanner message={error} onRetry={staleRetry ?? undefined} />
        </div>
      )}

      <ul className="mt-4 flex flex-col gap-3">
        {poll.proposedSlots.map((slot) => {
          const isLockedSlot = poll.lockedSlotId === slot.id;
          return (
            <li
              key={slot.id}
              className={
                'rounded-xl2 border p-3 ' +
                (isLockedSlot ? 'border-coral-400 bg-coral-400/10' : 'border-sand-200 bg-sand-50/60')
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink-900">{formatSlot(slot.startISO, slot.endISO)}</span>
                {isLockedSlot && <span className="text-xs font-semibold text-coral-600">Chosen</span>}
              </div>

              {!locked && (
                <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Your vote">
                  {VOTES.map((v) => {
                    const active = myVotes.get(slot.id) === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        disabled={busy}
                        aria-pressed={active}
                        onClick={() => vote(slot.id, v)}
                        className={
                          'min-h-[44px] flex-1 rounded-xl2 px-3 text-sm font-semibold transition ' +
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-500 ' +
                          'disabled:opacity-50 ' +
                          (active
                            ? 'bg-coral-500 text-white'
                            : 'bg-sand-100 text-ink-700 hover:bg-sand-200')
                        }
                      >
                        {VOTE_LABEL[v]}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-2 flex flex-col gap-0.5 text-xs text-ink-700/70">
                {VOTES.map((v) => {
                  const who = tally(slot.id, v);
                  if (who.length === 0) return null;
                  return (
                    <span key={v}>
                      <span className="font-semibold">{VOTE_LABEL[v]}:</span> {who.join(', ')}
                    </span>
                  );
                })}
              </div>

              {isOwner && !locked && (
                <div className="mt-3">
                  <Button variant="secondary" busy={busy} onClick={() => lock(slot.id)}>
                    Lock this slot
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {locked && (
        <p className="mt-3 text-sm text-ink-700/70">
          The time is locked in. Next up: pick what you’ll do together.
        </p>
      )}
    </Card>
  );
}
